import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { API_URL } from "../config/api";
import {
  Copy,
  Check,
  Ship,
  RefreshCw,
  Calendar,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Globe,
  Shield,
  Code,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";


// Code block component with copy functionality
function CodeBlock({ code, language = "json" }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-slate-700 hover:bg-slate-600 rounded transition-colors opacity-0 group-hover:opacity-100"
      >
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-slate-300" />}
      </button>
    </div>
  );
}

// Expandable section component
function ExpandableSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-emerald-600" />
          <span className="font-semibold text-slate-800">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronRight className="h-5 w-5 text-slate-500" />}
      </button>
      {isOpen && <div className="p-4 border-t bg-white">{children}</div>}
    </div>
  );
}

export default function PublicAPIDocsPage() {
  const baseUrl = API_URL;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Simple Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://i.imgur.com/Yqk8xYL.png" 
              alt="myEuropacific" 
              className="h-8"
            />
            <span className="text-slate-300">|</span>
            <span className="font-semibold text-slate-700">API Documentation</span>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="text-slate-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to myEuropacific
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full mb-4">
            <Code className="h-5 w-5 text-emerald-600" />
            <span className="text-emerald-700 font-medium">LCL Status Integration API</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Connect Your Systems to <span className="text-emerald-600">myEuropacific</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Push shipment data, receive status updates, and automate your logistics workflow with our REST API.
          </p>
        </div>

        {/* Quick Start */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-8 text-white mb-12">
          <h2 className="text-2xl font-bold mb-4">Quick Start</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">1</div>
              <p className="text-emerald-100">Get your API key from myEuropacific team</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">2</div>
              <p className="text-emerald-100">Include the key in X-API-Key header</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-3xl font-bold mb-1">3</div>
              <p className="text-emerald-100">Start pushing shipment data via POST</p>
            </div>
          </div>
        </div>

        {/* Base URL */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-3">Base URL</h3>
          <div className="flex items-center gap-2">
            <code className="bg-slate-900 text-emerald-400 px-4 py-3 rounded-lg font-mono text-sm flex-1">
              {baseUrl}/api/lcl-status
            </code>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => copyToClipboard(`${baseUrl}/api/lcl-status`)}
              className="shrink-0"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Authentication */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Shield className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Authentication</h3>
              <p className="text-amber-700 mb-4">
                All API requests must include the <code className="bg-amber-100 px-2 py-0.5 rounded text-amber-800 font-mono text-sm">X-API-Key</code> header with your API key.
              </p>
              <CodeBlock code={`curl -X POST "${baseUrl}/api/lcl-status/shipments" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key_here" \\
  -d '{ ... }'`} />
            </div>
          </div>
        </div>

        {/* Endpoints */}
        <div className="space-y-6 mb-12">
          <h2 className="text-2xl font-bold text-slate-900">API Endpoints</h2>
          
          {/* POST /shipments */}
          <ExpandableSection title="POST /shipments — New Shipment Notification" icon={Ship} defaultOpen={true}>
            <div className="space-y-4">
              <p className="text-slate-600">
                Notify myEuropacific about a new shipment. The system will automatically match by HBL number 
                or add to review queue if no match is found.
              </p>
              
              <div>
                <h5 className="font-semibold text-slate-700 mb-2">Request Body</h5>
                <CodeBlock code={`{
  "hbl_number": "HBL2024001",          // Required - House BL number
  "mbl_number": "MBL2024001",          // Optional - Master BL number
  "booking_number": "BK123456",        // Optional
  
  "origin_port": "CNSHA",              // Required - Port code
  "origin_port_name": "Shanghai",      // Optional
  "destination_port": "SIKOP",         // Required - Port code (Koper)
  "destination_port_name": "Koper",    // Optional
  
  "shipper": {
    "name": "Shanghai Electronics Co", // Required
    "address": "123 Export St",        // Optional
    "country": "China",                // Optional
    "contact": "John Doe",             // Optional
    "email": "export@company.cn"       // Optional
  },
  
  "consignee": {
    "name": "EuroPacific Slovenia",    // Required
    "address": "456 Import Ave",       // Optional
    "country": "Slovenia",             // Optional
    "contact": "Jane Smith",           // Optional
    "email": "import@europacific.si"   // Optional
  },
  
  "cargo": {
    "description": "Electronic Parts", // Required
    "commodity": "Electronics",        // Optional
    "hs_code": "8542.31",             // Optional
    "weight_kg": 15000,               // Optional
    "volume_cbm": 32.5,               // Optional
    "packages": 50,                   // Optional
    "package_type": "Cartons",        // Optional
    "is_hazardous": false,            // Optional
    "un_number": null                 // Optional - for DG cargo
  },
  
  "containers": [
    {
      "container_number": "MSCU1234567",
      "container_type": "40HC",
      "seal_number": "SEAL123",
      "weight_kg": 15500
    }
  ],
  
  "vessel_name": "MSC AURORA",         // Optional
  "voyage_number": "FE401W",           // Optional
  "etd": "2026-03-10T00:00:00Z",       // Optional - ISO 8601
  "eta": "2026-04-05T00:00:00Z",       // Optional - ISO 8601
  "service_type": "LCL",               // Optional - FCL or LCL
  "incoterm": "FOB",                   // Optional
  "remarks": "Handle with care"        // Optional
}`} />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-semibold text-slate-700 mb-2">Success Response (Matched)</h5>
                  <CodeBlock code={`{
  "status": "linked",
  "message": "Shipment linked to existing PF",
  "pf_id": "64abc123def456789",
  "pf_number": "PF-000123-26"
}`} />
                </div>
                <div>
                  <h5 className="font-semibold text-slate-700 mb-2">Success Response (Queued)</h5>
                  <CodeBlock code={`{
  "status": "queued",
  "message": "No matching PF found",
  "queue_id": "64xyz789abc123456",
  "suggestions": [...]
}`} />
                </div>
              </div>
            </div>
          </ExpandableSection>
          
          {/* POST /status-updates */}
          <ExpandableSection title="POST /status-updates — Status Update" icon={RefreshCw}>
            <div className="space-y-4">
              <p className="text-slate-600">
                Send status updates for a shipment (loaded at POL, departed, arrived at transshipment, ETA updates, etc.)
              </p>
              
              <div>
                <h5 className="font-semibold text-slate-700 mb-2">Request Body</h5>
                <CodeBlock code={`{
  "hbl_number": "HBL2024001",          // Required
  "mbl_number": "MBL2024001",          // Optional
  "container_number": "MSCU1234567",   // Optional
  
  "status_code": "LOADED",             // Required - See codes below
  "status_description": "Container loaded at Shanghai Port",
  "status_datetime": "2026-03-10T14:30:00Z",
  
  "location_code": "CNSHA",            // Optional
  "location_name": "Shanghai",         // Optional
  
  "vessel_name": "MSC AURORA",         // Optional
  "voyage_number": "FE401W",           // Optional
  
  "updated_etd": "2026-03-10T18:00:00Z",  // Optional
  "updated_eta": "2026-04-06T00:00:00Z",  // Optional
  
  "remarks": "On schedule"             // Optional
}`} />
              </div>
              
              <div>
                <h5 className="font-semibold text-slate-700 mb-2">Status Codes</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div className="bg-slate-50 p-2 rounded font-mono">BOOKED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">LOADED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">DEPARTED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">ARRIVED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">TS_ARRIVED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">TS_LOADED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">TS_DEPARTED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">ETA_UPDATE</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">DISCHARGED</div>
                  <div className="bg-slate-50 p-2 rounded font-mono">DELIVERED</div>
                </div>
              </div>
            </div>
          </ExpandableSection>
          
          {/* POST /schedules */}
          <ExpandableSection title="POST /schedules — Schedule Update" icon={Calendar}>
            <div className="space-y-4">
              <p className="text-slate-600">
                Update vessel schedule information including transshipment details and updated ETD/ETA.
              </p>
              
              <div>
                <h5 className="font-semibold text-slate-700 mb-2">Request Body</h5>
                <CodeBlock code={`{
  "hbl_number": "HBL2024001",          // Required
  "mbl_number": "MBL2024001",          // Optional
  
  "vessel_legs": [
    {
      "leg": 1,
      "vessel_name": "MSC AURORA",
      "voyage_number": "FE401W",
      "from_port": "CNSHA",
      "to_port": "SGSIN",
      "etd": "2026-03-10T00:00:00Z",
      "eta": "2026-03-18T00:00:00Z"
    },
    {
      "leg": 2,
      "vessel_name": "MSC MAYA",
      "voyage_number": "AE205E",
      "from_port": "SGSIN",
      "to_port": "SIKOP",
      "etd": "2026-03-20T00:00:00Z",
      "eta": "2026-04-05T00:00:00Z"
    }
  ],
  
  "etd_origin": "2026-03-10T00:00:00Z",
  "eta_transshipment": "2026-03-18T00:00:00Z",
  "etd_transshipment": "2026-03-20T00:00:00Z",
  "eta_destination": "2026-04-05T00:00:00Z",
  
  "transshipment_ports": ["SGSIN"],
  
  "remarks": "Schedule confirmed"
}`} />
              </div>
            </div>
          </ExpandableSection>
        </div>

        {/* Error Handling */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-12">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Error Responses</h3>
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">401 Unauthorized</p>
                <p className="text-sm text-red-600">Invalid or missing API key</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800">422 Validation Error</p>
                <p className="text-sm text-orange-600">Missing required fields or invalid data format</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg">
              <AlertCircle className="h-5 w-5 text-slate-500 mt-0.5" />
              <div>
                <p className="font-medium text-slate-800">500 Internal Server Error</p>
                <p className="text-sm text-slate-600">Server-side error - please contact support</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white text-center">
          <Globe className="h-12 w-12 mx-auto mb-4 text-emerald-400" />
          <h3 className="text-2xl font-bold mb-2">Need Help?</h3>
          <p className="text-slate-300 mb-6 max-w-lg mx-auto">
            Contact our integration team for API keys, technical support, or to request additional endpoints.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:info@europacific.com">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <ExternalLink className="h-4 w-4 mr-2" />
                info@europacific.com
              </Button>
            </a>
            <Link to="/login">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent">
                Staff Login
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-5xl mx-auto px-6 py-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} EuroPacific d.o.o. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
